import api from '../../../services/api/api';

export const resumeMarketingService = {
  getDashboard: async () => {
    const response = await api.get('/resume-marketing/dashboard');
    return response.data;
  },

  getAllResumes: async (params) => {
    const response = await api.get('/resume-marketing/resumes', { params });
    return response.data;
  },

  createResume: async (data) => {
    const response = await api.post('/resume-marketing/resumes', data);
    return response.data;
  },

  updateResume: async (id, data) => {
    const response = await api.put(`/resume-marketing/resumes/${id}`, data);
    return response.data;
  },

  approveResume: async (id) => {
    const response = await api.post(`/resume-marketing/resumes/${id}/approve`);
    return response.data;
  },

  getAllClients: async (params) => {
    const response = await api.get('/resume-marketing/clients', { params });
    return response.data;
  },

  createClient: async (data) => {
    const response = await api.post('/resume-marketing/clients', data);
    return response.data;
  },

  submitCandidateToClient: async (clientId, data) => {
    const response = await api.post(`/resume-marketing/clients/${clientId}/submit-candidate`, data);
    return response.data;
  },

  getInterviewTracking: async () => {
    const response = await api.get('/resume-marketing/interview-tracking');
    return response.data;
  },

  bulkUploadAssignedCandidates: async (file, defaultAssignedTo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('defaultAssignedTo', defaultAssignedTo);
    const response = await api.post('/resume-marketing/assigned-candidates/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Client Submissions (Individual Marketing Rep + TLs)
  getAllClientSubmissions: async (params = { candidateName: '', position: '' }) => {
    const response = await api.get('/resume-marketing/client-submissions', { params });
    return response.data;
  },
  getClientSubmissionById: async (id) => {
    const response = await api.get(`/resume-marketing/client-submissions/${id}`);
    return response.data;
  },
  createClientSubmission: async (data) => {
    const response = await api.post('/resume-marketing/client-submissions', data);
    return response.data;
  },
  updateClientSubmission: async (id, data) => {
    const response = await api.put(`/resume-marketing/client-submissions/${id}`, data);
    return response.data;
  },

  // Assigned Candidates (Individual Marketing Rep + TLs)
  getAllAssignedCandidates: async (params) => {
    const response = await api.get('/resume-marketing/assigned-candidates', { params });
    return response.data;
  },
  createAssignedCandidate: async (data) => {
    const response = await api.post('/resume-marketing/assigned-candidates', data);
    return response.data;
  },
  updateAssignedCandidate: async (id, data) => {
    const response = await api.put(`/resume-marketing/assigned-candidates/${id}`, data);
    return response.data;
  },
  deleteAssignedCandidate: async (id) => {
    const response = await api.delete(`/resume-marketing/assigned-candidates/${id}`);
    return response.data;
  },
  updateAssignedCandidateStatus: async (id, data) => {
    const response = await api.patch(`/resume-marketing/assigned-candidates/${id}/status`, data);
    return response.data;
  },

  /** Assigned candidate details (TL: team scope; Employee: own; Admin: all) */
  getMarketingCandidateById: async (id) => {
    const response = await api.get(`/resume-marketing/candidates/${id}`);
    return response.data;
  },

  // Resume Tracking (TL assigns; employee updates own)
  getAllResumeTracking: async (params) => {
    const response = await api.get('/resume-marketing/resume-tracking', { params });
    return response.data;
  },
  getResumeTrackingAssignees: async () => {
    const response = await api.get('/resume-marketing/resume-tracking/assignees');
    return response.data;
  },
  getResumeTrackingById: async (id) => {
    const response = await api.get(`/resume-marketing/resume-tracking/${id}`);
    return response.data;
  },
  createResumeTracking: async (data) => {
    const response = await api.post('/resume-marketing/resume-tracking', data);
    return response.data;
  },
  updateResumeTracking: async (id, data) => {
    const response = await api.put(`/resume-marketing/resume-tracking/${id}`, data);
    return response.data;
  },
  deleteResumeTracking: async (id) => {
    const response = await api.delete(`/resume-marketing/resume-tracking/${id}`);
    return response.data;
  },

  bulkUploadResumeTracking: async (file, { skipDuplicates = true } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('skipDuplicates', skipDuplicates ? 'true' : 'false');
    const response = await api.post('/resume-marketing/resume-tracking/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Interview Tracking (All employees)
  getAllInterviewTracking: async (params) => {
    const response = await api.get('/resume-marketing/interview-tracking', { params });
    return response.data;
  },
  getInterviewTrackingById: async (id) => {
    const response = await api.get(`/resume-marketing/interview-tracking/${id}`);
    return response.data;
  },
  createInterviewTracking: async (data) => {
    const response = await api.post('/resume-marketing/interview-tracking', data);
    return response.data;
  },
  updateInterviewTracking: async (id, data) => {
    const response = await api.put(`/resume-marketing/interview-tracking/${id}`, data);
    return response.data;
  },
  deleteInterviewTracking: async (id) => {
    const response = await api.delete(`/resume-marketing/interview-tracking/${id}`);
    return response.data;
  },

  // Monthly Performance (All)
  getMonthlyPerformance: async (params) => {
    const response = await api.get('/resume-marketing/monthly-performance', { params });
    return response.data;
  },

  // Daily Submissions (All)
  getDailySubmissions: async (params) => {
    const response = await api.get('/resume-marketing/daily-submissions', { params });
    return response.data;
  },

  /** SSO into Resume Builder — returns { url } for browser redirect */
  createResumeBuilderSso: async (options = {}) => {
    const response = await api.post('/resume-marketing/resume-builder/sso', options);
    return response.data;
  },

  /** Resumes created in Resume Builder for the logged-in user */
  getResumeBuilderResumes: async () => {
    const response = await api.get('/resume-marketing/resume-builder/resumes');
    return response.data;
  },

  deleteResumeBuilder: async (resumeId) => {
    const response = await api.delete(`/resume-marketing/resume-builder/resumes/${resumeId}`);
    return response.data;
  },

  getResumeBuilderAtsScore: async (resumeId) => {
    const response = await api.get(`/resume-marketing/resume-builder/resumes/${resumeId}/ats-score`);
    return response.data;
  },

  checkResumeBuilderAts: async (resumeId, jobDescription = '') => {
    const response = await api.post(`/resume-marketing/resume-builder/resumes/${resumeId}/ats-check`, {
      jobDescription,
    });
    return response.data;
  },

  uploadResumeBuilder: async (file) => {
    const formData = new FormData();
    formData.append('resume', file);
    const response = await api.post('/resume-marketing/resume-builder/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  checkResumeBuilderAtsUpload: async (file, jobDescription = '') => {
    const formData = new FormData();
    formData.append('resume', file);
    if (jobDescription?.trim()) {
      formData.append('jobDescription', jobDescription.trim());
    }
    const response = await api.post('/resume-marketing/resume-builder/ats-check-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
